import Step from '../../constants/Step'
import { Column } from '../column/Column'
import { createOrder, IOrderFactory, Order } from '../order/Order'

export interface ICycle {
    acceptanceDeadline: null | Date
    code: string
    name: string
    pickupFrom: Date
    pickupTo: Date
    pickupX: number
    numberOfOrders: number
    numberOfOrdersLabeled: number
    numberOfProductsLabeled: number
    numberOfProducts: number
    numberOfProductsReady: number
    numberOfOpenOrders: number
    totalNumberOfOpenOrders: number
    totalNumberProducts: number
    totalNumberProductsLabeled: number
    orders: Order[]
}

export interface ICycleFactory extends Omit<ICycle, 'orders'> {
    orders: IOrderFactory[]
}

export class Cycle {
    acceptanceDeadline: null | Date
    code: string
    name: string
    pickupFrom: Date
    pickupTo: Date
    pickupX: number
    numberOfOrders: number
    numberOfOrdersLabeled: number
    numberOfProductsLabeled: number
    numberOfProducts: number
    numberOfProductsReady: number
    numberOfOpenOrders: number
    totalNumberOfOpenOrders: number
    totalNumberProducts: number
    totalNumberProductsLabeled: number
    orders: Order[]

    _column: Column | null = null
    _productsAmount: number | null = null
    _progress: number | null = null

    constructor(cycleData: ICycle) {
        this.acceptanceDeadline = cycleData.acceptanceDeadline
        this.code = cycleData.code
        this.name = cycleData.name
        this.pickupFrom = cycleData.pickupFrom
        this.pickupTo = cycleData.pickupTo
        this.pickupX = cycleData.pickupX
        this.numberOfOrders = cycleData.numberOfOrders
        this.numberOfOrdersLabeled = cycleData.numberOfOrdersLabeled
        this.numberOfProductsLabeled = cycleData.numberOfProductsLabeled
        this.numberOfProducts = cycleData.numberOfProducts
        this.numberOfProductsReady = cycleData.numberOfProductsReady
        this.numberOfOpenOrders = cycleData.numberOfOpenOrders
        this.totalNumberOfOpenOrders = cycleData.totalNumberOfOpenOrders

        this.totalNumberProducts = cycleData.totalNumberProducts
        this.totalNumberProductsLabeled = cycleData.totalNumberProductsLabeled
        this.orders = cycleData.orders
    }

    get productsAmount(): number {
        if (this._productsAmount === null) {
            this._productsAmount = this.orders.reduce(
                (count, order) => count + order.products.length,
                0
            )
        }

        return this._productsAmount
    }

    get isReady(): boolean {
        return this.column.step === Step.READY
    }

    get isInProgress(): boolean {
        return this.column.step === Step.IN_PROGRESS
    }

    get isInIncoming(): boolean {
        return this.column.step === Step.INCOMING
    }

    get progress(): number {
        if (this._progress === null) {
            if (this.column.step === Step.READY) {
                this._progress = this.totalNumberOfOpenOrders
                    ? (this.numberOfOrdersLabeled /
                          this.totalNumberOfOpenOrders) *
                      100
                    : 0
            } else {
                this._progress = this.productsAmount
                    ? (this.numberOfProductsLabeled / this.productsAmount) * 100
                    : 0
            }
        }

        return this._progress
    }

    setColumn(column: Column) {
        this._column = column
    }

    get column(): Column {
        if (!this._column) {
            throw new Error('Orphan cycle wa found: no column is set')
        }
        return this._column
    }

    get numberOfCanceledProducts(): number {
        return this.orders.reduce(
            (numberOfCanceledProducts, order) =>
                numberOfCanceledProducts +
                order.products.filter((product) => product.isCanceled).length,
            0
        )
    }
}

export const createCycle = (cycleData: ICycleFactory): Cycle => {
    const orders = cycleData.orders.map(createOrder)
    const cycle = new Cycle({
        ...cycleData,
        orders,
    })
    orders.forEach((order) => order.setCycle(cycle))
    return cycle
}
